/**
 * Location tab — AOI 7-parameter scorecard for a map pin + radius.
 * Uses the same formulas as the district Calculator (not Daewoo Financial Hub metrics).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    MapPin, Search, RefreshCw, Save, Trash2, History, ArrowLeft,
    BarChart3, AlertCircle, Loader2, X, Download, ExternalLink,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import LocationPropertyForm, {
    emptySiteDetails,
    emptyCommercialDetails,
    buildSiteDetailsFromFetch,
} from './LocationPropertyForm';
import { AoiFetchSummary, AoiFormulaBreakdown, AoiHealthPlacesSection, AoiHealthVerifyModal, resolveHealthPlaceCounts, HEALTH_PLACE_FIELDS } from '../aoi/AoiUiParts';
import {
    emptyAoiInputs,
    SEARCH_RADIUS_PRESETS,
    effectiveSearchRadiusKm,
    formatSearchRadius,
    aoiScoreTone,
    searchRadiusPresetLabel,
} from '../../constants/aoiCategories';

export default function LocationScorecardTab() {
    const [view, setView] = useState('new');
    const [locationInput, setLocationInput] = useState('');
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [inputs, setInputs] = useState(emptyAoiInputs());
    const [notes, setNotes] = useState('');
    const [preview, setPreview] = useState({ aoiScore: 0, categoryScores: {}, centrePotential: '', territoryType: '' });
    const [fetched, setFetched] = useState(null);
    const [fetching, setFetching] = useState(false);
    const [healthPlaces, setHealthPlaces] = useState(null);
    const [verifyCategory, setVerifyCategory] = useState(null);
    const [saving, setSaving] = useState(false);
    const [downloadingLive, setDownloadingLive] = useState(false);
    const [downloadingHistory, setDownloadingHistory] = useState(false);
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [selectedHistory, setSelectedHistory] = useState(null);
    const [mapsReady, setMapsReady] = useState(false);
    const [searchRadiusMode, setSearchRadiusMode] = useState('2km');
    const [searchRadiusManual, setSearchRadiusManual] = useState('3');
    const [resolvedState, setResolvedState] = useState('');
    const [resolvedDistrict, setResolvedDistrict] = useState('');
    const [siteDetails, setSiteDetails] = useState(emptySiteDetails());
    const [commercialDetails, setCommercialDetails] = useState(emptyCommercialDetails());

    const inputRef = useRef(null);
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markerRef = useRef(null);
    const circleRef = useRef(null);
    const debounceRef = useRef(null);

    const searchRadiusKm = useMemo(
        () => effectiveSearchRadiusKm(searchRadiusMode, searchRadiusManual),
        [searchRadiusMode, searchRadiusManual],
    );

    const mapsKeyMissing = !import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

    useEffect(() => {
        const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
        if (!apiKey) return;
        if (window.google?.maps) { setMapsReady(true); return; }
        window.__onGoogleMapsLoadedLocAoi = () => setMapsReady(true);
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=__onGoogleMapsLoadedLocAoi`;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
    }, []);

    useEffect(() => {
        if (!mapsReady || !inputRef.current) return;
        const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
            componentRestrictions: { country: 'IN' },
            fields: ['name', 'formatted_address', 'geometry', 'place_id'],
        });
        ac.addListener('place_changed', () => {
            const place = ac.getPlace();
            if (!place.geometry?.location) return;
            setSelectedPlace({
                name: place.name || place.formatted_address,
                address: place.formatted_address,
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
                placeId: place.place_id,
            });
            setLocationInput(place.formatted_address);
            setFetched(null);
            setHealthPlaces(null);
            setVerifyCategory(null);
            setResolvedState('');
            setResolvedDistrict('');
            setSiteDetails(emptySiteDetails());
            setCommercialDetails(emptyCommercialDetails());
        });
    }, [mapsReady]);

    useEffect(() => {
        if (!mapsReady || !mapRef.current || !selectedPlace) return;
        const center = { lat: selectedPlace.lat, lng: selectedPlace.lng };
        if (!mapInstanceRef.current) {
            mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
                center,
                zoom: 14,
                mapTypeId: 'hybrid',
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: true,
                fullscreenControlOptions: { position: window.google.maps.ControlPosition.RIGHT_BOTTOM },
            });
        } else {
            mapInstanceRef.current.setCenter(center);
            mapInstanceRef.current.setZoom(14);
            mapInstanceRef.current.setMapTypeId('hybrid');
        }
        if (markerRef.current) markerRef.current.setMap(null);
        markerRef.current = new window.google.maps.Marker({
            position: center, map: mapInstanceRef.current, title: selectedPlace.name,
        });
    }, [selectedPlace, mapsReady]);

    useEffect(() => {
        if (!mapsReady || !mapInstanceRef.current || !selectedPlace) return;
        if (circleRef.current) circleRef.current.setMap(null);
        circleRef.current = new window.google.maps.Circle({
            map: mapInstanceRef.current,
            center: { lat: selectedPlace.lat, lng: selectedPlace.lng },
            radius: searchRadiusKm * 1000,
            strokeColor: '#0ea5e9',
            strokeOpacity: 0.85,
            strokeWeight: 2,
            fillColor: '#0ea5e9',
            fillOpacity: 0.12,
        });
        mapInstanceRef.current.fitBounds(circleRef.current.getBounds());
    }, [searchRadiusKm, selectedPlace, mapsReady]);

    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            api.post('/aoi/compute', inputs, { silent: true })
                .then(r => setPreview(r.data))
                .catch(() => {});
        }, 200);
        return () => clearTimeout(debounceRef.current);
    }, [inputs]);

    /**
     * @param {boolean} force  Bypass the server's 24h cache and re-query the
     *                         providers. The plain Fetch button leaves this
     *                         off so an unchanged pin + radius returns from
     *                         cache instantly; "Refresh" turns it on.
     */
    const autoFetch = async ({ force = false } = {}) => {
        if (!selectedPlace) return toast.error('Pick a location first');
        setFetching(true);
        setHealthPlaces(null);
        setVerifyCategory(null);

        /* Radius changes invalidate the previous fetch — the catchment is a
           different area, so cached counts no longer describe it. */
        const radiusChanged = fetched?.searchRadiusKm != null
            && fetched.searchRadiusKm !== searchRadiusKm;

        try {
            /* One request. Health places now come back in the same payload,
               so we no longer fire a parallel call that repeats the same
               Google Places lookups. */
            const { data } = await api.post('/aoi/autofetch', {
                lat: selectedPlace.lat,
                lon: selectedPlace.lng,
                locationQuery: selectedPlace.address || selectedPlace.name,
                radiusKm: searchRadiusKm,
                force: force || radiusChanged,
                includeHealthPlaces: true,
            }, { timeout: 90_000 });

            if (data.resolvedTerritory?.state) setResolvedState(data.resolvedTerritory.state);
            if (data.resolvedTerritory?.district) setResolvedDistrict(data.resolvedTerritory.district);

            setFetched(data);
            if (data.healthPlaces) setHealthPlaces(data.healthPlaces);

            setSiteDetails(buildSiteDetailsFromFetch({
                selectedPlace,
                resolvedLocation: data.resolvedLocation,
                resolvedState: data.resolvedTerritory?.state || data.resolvedLocation?.state || '',
                resolvedDistrict: data.resolvedTerritory?.district
                    || data.resolvedLocation?.territoryDistrict
                    || data.resolvedLocation?.city
                    || '',
            }));

            /* Auto-fetch always replaces the sub-scores for the pin it just
               measured — keeping stale values from a previous location would
               silently mix two places into one score. */
            setInputs(() => {
                const next = emptyAoiInputs();
                for (const [cat, subs] of Object.entries(data.suggestions || {})) {
                    next[cat] = { ...next[cat] };
                    for (const [k, v] of Object.entries(subs || {})) {
                        next[cat][k] = v;
                    }
                }
                return next;
            });

            const sources = [
                data.providers?.osm?.counts ? 'OSM' : null,
                data.providers?.wiki?.stats ? 'Wikidata' : null,
                data.providers?.google?.counts ? 'Google Places' : null,
            ].filter(Boolean).join(' + ');

            const secs = data.tookMs != null ? ` (${(data.tookMs / 1000).toFixed(1)}s)` : '';

            if (data.cached) {
                toast.success(`Loaded cached result for ${formatSearchRadius(searchRadiusKm)} · use Refresh for live data`);
            } else if (data.degraded) {
                const down = Object.entries(data.providerStatus || {})
                    .filter(([, v]) => v !== 'ok')
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(', ');
                toast(`Partial result — scored from ${sources || 'proxies'}. ${down}`, { icon: '⚠️' });
            } else {
                toast.success(`Fetched within ${formatSearchRadius(searchRadiusKm)} · ${sources || 'proxies'}${secs}`);
            }
        } catch (e) {
            const timedOut = e.code === 'ECONNABORTED' || /timeout/i.test(e.message || '');
            toast.error(timedOut
                ? 'Fetch timed out — the map data providers are slow right now. Try again.'
                : (e?.response?.data?.error || 'Fetch failed'));
        } finally {
            setFetching(false);
        }
    };

    const resetForm = () => {
        setSelectedPlace(null);
        setLocationInput('');
        setInputs(emptyAoiInputs());
        setNotes('');
        setFetched(null);
        setHealthPlaces(null);
        setVerifyCategory(null);
        setResolvedState('');
        setResolvedDistrict('');
        setSiteDetails(emptySiteDetails());
        setCommercialDetails(emptyCommercialDetails());
        setSearchRadiusMode('2km');
        setSearchRadiusManual('3');
        if (markerRef.current) markerRef.current.setMap(null);
        if (circleRef.current) circleRef.current.setMap(null);
        mapInstanceRef.current = null;
    };

    const buildHealthSummary = useCallback(() => {
        const unavailable = fetched?.healthUnavailable || [];
        const counts = resolveHealthPlaceCounts(fetched, healthPlaces, unavailable);
        const verifyCounts = {};
        /* Carry the actual venues, not just the totals, so the PDF can list
           which places make up each count. Trimmed to the fields the export
           renders — the raw Google payload is far larger than we need. */
        const places = {};
        for (const field of HEALTH_PLACE_FIELDS) {
            const list = healthPlaces?.[field.key];
            if (!Array.isArray(list)) continue;
            verifyCounts[field.key] = list.length;
            places[field.key] = list.map(p => ({
                name: p.name || '',
                address: p.address || '',
                rating: p.rating ?? null,
                open: p.open ?? null,
                status: p.status || '',
                mapsUrl: p.mapsUrl || '',
            }));
        }
        return {
            radiusLabel: formatSearchRadius(searchRadiusKm),
            locationName: selectedPlace?.name || '',
            counts,
            verifyCounts,
            places,
            unavailable,
        };
    }, [fetched, healthPlaces, searchRadiusKm, selectedPlace]);

    const handleSave = async () => {
        if (!selectedPlace) return;
        setSaving(true);
        try {
            await api.post('/location-scorecard/save', {
                locationName: selectedPlace.name,
                address: selectedPlace.address,
                placeId: selectedPlace.placeId,
                lat: selectedPlace.lat,
                lng: selectedPlace.lng,
                searchRadiusKm,
                state: resolvedState,
                district: resolvedDistrict,
                notes,
                ...inputs,
                healthSummary: buildHealthSummary(),
                siteDetails,
                commercialDetails,
            });
            toast.success(`Saved · AOI ${preview.aoiScore}`);
            setView('history');
            setSelectedHistory(null);
            loadHistory();
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const loadHistory = useCallback(async () => {
        setLoadingHistory(true);
        try {
            const { data } = await api.get('/location-scorecard', { params: { limit: 50 } });
            setHistory(data.items || []);
        } catch {
            setHistory([]);
        } finally {
            setLoadingHistory(false);
        }
    }, []);

    useEffect(() => {
        if (view === 'history') loadHistory();
    }, [view, loadHistory]);

    const handleDelete = async (id) => {
        if (!confirm('Delete this location scorecard?')) return;
        try {
            await api.delete(`/location-scorecard/${id}`);
            setHistory(h => h.filter(x => x._id !== id));
            if (selectedHistory?._id === id) setSelectedHistory(null);
            toast.success('Deleted');
        } catch {
            toast.error('Delete failed');
        }
    };

    const downloadBlob = (blob, filename) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    const ensurePdfBlob = async (data) => {
        const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
        const head = await blob.slice(0, 5).text();
        if (!head.startsWith('%PDF')) {
            let message = 'Download failed — server did not return a PDF. Restart the backend and try again.';
            try {
                const text = await blob.text();
                const parsed = JSON.parse(text);
                if (parsed?.error) message = parsed.error;
            } catch { /* not JSON */ }
            throw new Error(message);
        }
        return new Blob([blob], { type: 'application/pdf' });
    };

    const downloadLive = async () => {
        if (!selectedPlace) return toast.error('Pick a location first');
        setDownloadingLive(true);
        try {
            const res = await api.post('/location-scorecard/export/live', {
                locationName: selectedPlace.name,
                address: selectedPlace.address,
                lat: selectedPlace.lat,
                lng: selectedPlace.lng,
                searchRadiusKm,
                state: resolvedState,
                district: resolvedDistrict,
                notes,
                inputs,
                healthSummary: buildHealthSummary(),
                siteDetails,
                commercialDetails,
            }, { responseType: 'blob', timeout: 120_000, silent: true });
            const blob = await ensurePdfBlob(res.data);
            const safe = (s) => String(s || '').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '');
            downloadBlob(blob, `location-aoi-${safe(selectedPlace.name)}-${new Date().toISOString().slice(0, 10)}.pdf`);
            toast.success('Downloaded location AOI PDF');
        } catch (e) {
            toast.error(e?.message || e?.response?.data?.error || 'Download failed');
        } finally {
            setDownloadingLive(false);
        }
    };

    const downloadHistoryItem = async (item) => {
        if (!item?._id) return;
        setDownloadingHistory(true);
        try {
            const res = await api.get(`/location-scorecard/${item._id}/export`, {
                responseType: 'blob',
                timeout: 120_000,
                silent: true,
            });
            const blob = await ensurePdfBlob(res.data);
            const safe = (s) => String(s || '').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '');
            downloadBlob(blob, `location-aoi-${safe(item.locationName)}-${new Date().toISOString().slice(0, 10)}.pdf`);
            toast.success('Downloaded location AOI PDF');
        } catch (e) {
            toast.error(e?.message || e?.response?.data?.error || 'Download failed');
        } finally {
            setDownloadingHistory(false);
        }
    };

    const step = !selectedPlace ? 1 : !fetched ? 2 : 3;

    return (
        <div className="space-y-3">
            {/* Tabs */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="inline-flex rounded-xl bg-white border border-ink-100 p-1 shadow-soft">
                    {[
                        { id: 'new', label: 'New evaluation', icon: MapPin },
                        { id: 'history', label: 'History', icon: History },
                    ].map(t => (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => { setView(t.id); setSelectedHistory(null); }}
                            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition
                                ${view === t.id ? 'bg-brand-teal text-white shadow-sm' : 'text-ink-600 hover:bg-ink-50'}`}
                        >
                            <t.icon className="h-3.5 w-3.5" /> {t.label}
                        </button>
                    ))}
                </div>
                {view === 'new' && (
                    <ol className="hidden md:flex items-center gap-1.5 text-[11px] font-semibold text-ink-400">
                        {[
                            { n: 1, label: 'Locate' },
                            { n: 2, label: 'Fetch' },
                            { n: 3, label: 'Property & save' },
                        ].map((s, i) => (
                            <li key={s.n} className="flex items-center gap-1.5">
                                {i > 0 && <span className="text-ink-200">·</span>}
                                <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-bold
                                    ${step >= s.n ? 'bg-brand-teal text-white' : 'bg-ink-100 text-ink-400'}`}>
                                    {s.n}
                                </span>
                                <span className={step >= s.n ? 'text-ink-700' : ''}>{s.label}</span>
                            </li>
                        ))}
                    </ol>
                )}
            </div>

            {mapsKeyMissing && (
                <div className="rounded-xl p-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    Set <code className="font-mono">VITE_GOOGLE_MAPS_API_KEY</code> for location search and map preview.
                </div>
            )}

            {view === 'new' && (
                <div className="space-y-4">
                    {/* ── 1. Locate: search controls + map side by side ── */}
                    <section className="ag-card overflow-hidden shadow-soft">
                        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] xl:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
                            <div className="p-4 sm:p-5 space-y-3 border-b lg:border-b-0 lg:border-r border-ink-100 bg-white">
                                <div className="flex items-center justify-between gap-2">
                                    <div>
                                        <h3 className="font-bold text-sm text-ink-900">1 · Locate</h3>
                                        <p className="text-[11px] text-ink-400 mt-0.5">Search, set radius, fetch catchment</p>
                                    </div>
                                    {selectedPlace && (
                                        <button type="button" onClick={resetForm} className="text-xs text-ink-400 flex items-center gap-1 hover:text-ink-700">
                                            <X className="h-3.5 w-3.5" /> Reset
                                        </button>
                                    )}
                                </div>

                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400 pointer-events-none" />
                                    <input
                                        ref={inputRef}
                                        value={locationInput}
                                        onChange={e => setLocationInput(e.target.value)}
                                        disabled={!mapsReady && !mapsKeyMissing}
                                        placeholder={mapsReady ? 'Search area, locality, city…' : 'Loading Google Maps…'}
                                        className="ag-input pl-9 w-full text-sm py-2.5"
                                    />
                                </div>

                                {selectedPlace ? (
                                    <>
                                        <div className="p-2.5 rounded-xl bg-sky-50 border border-sky-100 flex gap-2 items-start">
                                            <MapPin className="h-4 w-4 text-sky-600 shrink-0 mt-0.5" />
                                            <div className="min-w-0 flex-1">
                                                <div className="font-semibold text-sm text-sky-900 leading-snug">{selectedPlace.name}</div>
                                                <div className="text-[11px] text-ink-500 mt-0.5 flex items-start gap-1.5">
                                                    <span className="min-w-0 line-clamp-2">{selectedPlace.address}</span>
                                                    <a
                                                        href={`https://www.google.com/maps/search/?api=1&query=${selectedPlace.lat},${selectedPlace.lng}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-sky-600 hover:text-sky-800 shrink-0"
                                                        title="Open in Google Maps"
                                                    >
                                                        <ExternalLink className="h-3.5 w-3.5" />
                                                    </a>
                                                </div>
                                                {(resolvedState || resolvedDistrict) && (
                                                    <div className="text-[10px] text-ink-400 mt-1">
                                                        Territory: {resolvedDistrict || '—'}{resolvedState ? `, ${resolvedState}` : ''}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div>
                                            <div className="text-[10px] font-bold uppercase tracking-wide text-ink-400 mb-1.5">Search radius</div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {SEARCH_RADIUS_PRESETS.filter(p => p.id !== 'manual').map(p => (
                                                    <button
                                                        key={p.id}
                                                        type="button"
                                                        onClick={() => setSearchRadiusMode(p.id)}
                                                        className={`px-2.5 py-1.5 rounded-lg text-xs font-bold border transition whitespace-nowrap
                                                            ${searchRadiusMode === p.id || (p.id === '1500m' && searchRadiusMode === '1.5km')
                                                                ? 'border-sky-500 bg-sky-50 text-sky-800'
                                                                : 'border-ink-200 bg-white text-ink-600 hover:bg-ink-50'}`}
                                                    >
                                                        <span className="tabular-nums">{searchRadiusPresetLabel(p)}</span>
                                                    </button>
                                                ))}
                                                <button
                                                    type="button"
                                                    onClick={() => setSearchRadiusMode('manual')}
                                                    className={`px-2.5 py-1.5 rounded-lg text-xs font-bold border transition
                                                        ${searchRadiusMode === 'manual'
                                                            ? 'border-sky-500 bg-sky-50 text-sky-800'
                                                            : 'border-ink-200 bg-white text-ink-600 hover:bg-ink-50'}`}
                                                >
                                                    Custom
                                                </button>
                                            </div>
                                            {searchRadiusMode === 'manual' && (
                                                <div className="flex items-center gap-2 mt-2">
                                                    <input
                                                        type="number"
                                                        min={0.2}
                                                        max={25}
                                                        step={0.1}
                                                        value={searchRadiusManual}
                                                        onChange={e => setSearchRadiusManual(e.target.value)}
                                                        className="ag-input text-sm w-24 py-1.5"
                                                        placeholder="e.g. 3"
                                                    />
                                                    <span className="text-xs text-ink-500 font-semibold">km</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                className="flex-1 ag-btn-primary justify-center py-2.5"
                                                disabled={fetching}
                                                onClick={() => autoFetch()}
                                            >
                                                {fetching
                                                    ? <Loader2 className="h-4 w-4 animate-spin" />
                                                    : <Search className="h-4 w-4" />}
                                                {fetching
                                                    ? `Fetching ${formatSearchRadius(searchRadiusKm)}…`
                                                    : `Fetch ${formatSearchRadius(searchRadiusKm)}`}
                                            </button>
                                            <button
                                                type="button"
                                                className="ag-btn-ghost justify-center py-2.5 px-3 shrink-0"
                                                disabled={fetching}
                                                onClick={() => autoFetch({ force: true })}
                                                title="Ignore cache and re-query providers"
                                            >
                                                <RefreshCw className={`h-4 w-4 ${fetching ? 'animate-spin' : ''}`} />
                                            </button>
                                        </div>

                                        <div>
                                            <label className="ag-label !mb-1">Notes</label>
                                            <textarea
                                                className="ag-input min-h-[64px] text-sm py-2"
                                                value={notes}
                                                onChange={e => setNotes(e.target.value)}
                                                placeholder="Optional notes…"
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <div className="rounded-xl border border-dashed border-ink-200 bg-ink-50/50 px-3 py-4 text-xs text-ink-500 leading-relaxed">
                                        Start typing a locality or landmark.
                                    </div>
                                )}
                            </div>

                            <div className="relative min-h-[280px] sm:min-h-[320px] lg:min-h-full bg-ink-50 self-stretch">
                                {!selectedPlace ? (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center text-ink-400 gap-2">
                                        <MapPin className="h-9 w-9 opacity-35" />
                                        <span className="text-xs font-medium">Map appears after you pick a place</span>
                                    </div>
                                ) : (
                                    <div ref={mapRef} className="absolute inset-0 w-full h-full min-h-[280px]" />
                                )}
                            </div>
                        </div>
                    </section>

                    {/* ── 2. Results: full-width score + formula + health (no orphan column) ── */}
                    {fetched ? (
                        <section className="space-y-3">
                            <div className="flex items-center justify-between gap-2 px-0.5">
                                <h3 className="font-bold text-sm text-ink-900">2 · Catchment results</h3>
                                <span className="text-[11px] text-ink-400 truncate">
                                    {formatSearchRadius(searchRadiusKm)}
                                    {selectedPlace?.name ? ` · ${selectedPlace.name}` : ''}
                                </span>
                            </div>

                            <AoiFormulaBreakdown
                                categoryScores={preview.categoryScores}
                                aoiScore={preview.aoiScore}
                                compact
                            />

                            <AoiHealthPlacesSection
                                fetched={fetched}
                                selectedPlace={selectedPlace}
                                fetching={fetching}
                                searchRadiusLabel={formatSearchRadius(searchRadiusKm)}
                                healthPlaces={healthPlaces}
                                onVerify={setVerifyCategory}
                                compact={false}
                            />

                            {(fetched.degraded || fetched.territoryResolved === false
                                || (fetched.searchRadiusKm != null && fetched.searchRadiusKm !== searchRadiusKm)) && (
                                <div className="space-y-2">
                                    {fetched.degraded && (
                                        <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
                                            <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                            <span>
                                                <strong>Partial data.</strong> Some sources didn&apos;t respond:{' '}
                                                {Object.entries(fetched.providerStatus || {})
                                                    .filter(([, v]) => v !== 'ok')
                                                    .map(([k, v]) => `${k} — ${v}`)
                                                    .join('; ')}
                                                . Try Refresh for full accuracy.
                                            </span>
                                        </div>
                                    )}
                                    {fetched.territoryResolved === false && (
                                        <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
                                            <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                            <span>
                                                Couldn&apos;t match this pin to a district. Nearby counts are accurate;
                                                tier estimates default to the lowest tier.
                                            </span>
                                        </div>
                                    )}
                                    {fetched.searchRadiusKm != null && fetched.searchRadiusKm !== searchRadiusKm && (
                                        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-2">
                                            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                                            Radius changed to {formatSearchRadius(searchRadiusKm)} — fetch again to refresh.
                                        </div>
                                    )}
                                </div>
                            )}

                            <AoiFetchSummary fetched={fetched} title={selectedPlace?.name} />
                        </section>
                    ) : selectedPlace ? (
                        <section className="ag-card p-5 shadow-soft border border-dashed border-ink-200 bg-ink-50/40">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                                <div className="h-10 w-10 rounded-xl bg-white border border-ink-100 flex items-center justify-center shrink-0">
                                    <BarChart3 className="h-5 w-5 text-brand-teal-700" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <h3 className="font-bold text-sm text-ink-900">2 · Catchment results</h3>
                                    <p className="text-xs text-ink-500 mt-0.5">
                                        Press <strong>Fetch {formatSearchRadius(searchRadiusKm)}</strong> to load AOI score,
                                        formula breakdown, and health &amp; wellness places for this pin.
                                    </p>
                                </div>
                            </div>
                        </section>
                    ) : null}

                    {verifyCategory && (
                        <AoiHealthVerifyModal
                            categoryKey={verifyCategory}
                            places={healthPlaces?.[verifyCategory] || []}
                            radiusLabel={formatSearchRadius(searchRadiusKm)}
                            onClose={() => setVerifyCategory(null)}
                        />
                    )}

                    {/* ── 3. Property + actions ── */}
                    {fetched && (
                        <section className="space-y-5">
                            <div className="flex items-center justify-between gap-2 px-0.5">
                                <h3 className="font-bold text-sm text-ink-900">3 · Site &amp; commercial</h3>
                                <span className="text-[11px] text-ink-400">Site auto-filled · commercial entered by you</span>
                            </div>

                            <LocationPropertyForm
                                siteDetails={siteDetails}
                                onSiteChange={setSiteDetails}
                                commercialDetails={commercialDetails}
                                onCommercialChange={setCommercialDetails}
                            />

                            <div className="ag-card mt-2 p-3.5 shadow-soft sticky bottom-3 z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border border-ink-100 bg-white/95 backdrop-blur-sm">
                                <p className="text-xs text-ink-500">
                                    Download PDF or save to History.
                                </p>
                                <div className="flex flex-wrap gap-2 shrink-0">
                                    <button
                                        type="button"
                                        className="ag-btn-ghost justify-center"
                                        onClick={downloadLive}
                                        disabled={downloadingLive || !selectedPlace}
                                    >
                                        <Download className="h-4 w-4" />
                                        {downloadingLive ? 'Downloading…' : 'Download PDF'}
                                    </button>
                                    <button
                                        type="button"
                                        className="ag-btn-primary justify-center"
                                        onClick={handleSave}
                                        disabled={saving || !selectedPlace}
                                    >
                                        <Save className="h-4 w-4" />
                                        {saving ? 'Saving…' : 'Save scorecard'}
                                    </button>
                                    <button
                                        type="button"
                                        className="ag-btn text-xs justify-center"
                                        onClick={() => { setView('history'); setSelectedHistory(null); }}
                                    >
                                        <History className="h-3.5 w-3.5" />
                                        View saved
                                    </button>
                                </div>
                            </div>
                        </section>
                    )}
                </div>
            )}

            {view === 'history' && (
                <div>
                    {selectedHistory ? (
                        <div className="space-y-5">
                            <div className="flex flex-wrap items-start gap-3">
                                <button type="button" onClick={() => setSelectedHistory(null)} className="ag-btn text-xs flex items-center gap-1">
                                    <ArrowLeft className="h-3.5 w-3.5" /> Back
                                </button>
                                <div className="flex-1 min-w-0">
                                    <h2 className="font-bold text-lg">{selectedHistory.locationName}</h2>
                                    <p className="text-sm text-ink-500">{selectedHistory.address}</p>
                                    <p className="text-xs text-ink-400 mt-1">
                                        {formatSearchRadius(selectedHistory.searchRadiusKm || 2)} radius
                                        {selectedHistory.district && ` · ${selectedHistory.district}, ${selectedHistory.state}`}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => downloadHistoryItem(selectedHistory)}
                                    disabled={downloadingHistory}
                                    className="ag-btn text-xs flex items-center gap-1"
                                >
                                    <Download className="h-3.5 w-3.5" />
                                    {downloadingHistory ? 'Downloading…' : 'Download PDF'}
                                </button>
                                <button type="button" onClick={() => handleDelete(selectedHistory._id)} className="ag-btn text-rose-700 bg-rose-50 text-xs">
                                    <Trash2 className="h-3.5 w-3.5 inline mr-1" /> Delete
                                </button>
                            </div>

                            <AoiFormulaBreakdown
                                categoryScores={selectedHistory.categoryScores}
                                aoiScore={selectedHistory.aoiScore}
                                compact
                            />

                            <AoiHealthPlacesSection
                                savedSummary={selectedHistory.healthSummary || {
                                    radiusLabel: formatSearchRadius(selectedHistory.searchRadiusKm || 2),
                                    locationName: selectedHistory.locationName || '',
                                    counts: {},
                                    unavailable: [],
                                }}
                            />

                            {(selectedHistory.siteDetails || selectedHistory.commercialDetails) && (
                                <LocationPropertyForm
                                    siteDetails={{ ...emptySiteDetails(), ...(selectedHistory.siteDetails || {}) }}
                                    onSiteChange={() => {}}
                                    commercialDetails={{ ...emptyCommercialDetails(), ...(selectedHistory.commercialDetails || {}) }}
                                    onCommercialChange={() => {}}
                                    readOnly
                                />
                            )}

                            {selectedHistory.notes && (
                                <div className="ag-card p-4 text-sm text-ink-600">
                                    <strong>Notes:</strong> {selectedHistory.notes}
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="font-bold text-lg flex items-center gap-2">
                                    <BarChart3 className="h-5 w-5 text-brand-teal-700" /> Saved locations
                                </h2>
                                <button type="button" onClick={loadHistory} className="ag-btn text-xs flex items-center gap-1">
                                    <RefreshCw className="h-3.5 w-3.5" /> Refresh
                                </button>
                            </div>
                            {loadingHistory ? (
                                <div className="text-center py-16 text-ink-400">
                                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" /> Loading…
                                </div>
                            ) : history.length === 0 ? (
                                <div className="ag-card p-12 text-center text-ink-500 text-sm">No saved location AOI scorecards yet.</div>
                            ) : (
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {history.map(item => (
                                        <div
                                            key={item._id}
                                            className="ag-card p-4 border border-transparent hover:border-brand-teal/30 transition flex flex-col gap-3"
                                        >
                                            <button
                                                type="button"
                                                onClick={() => setSelectedHistory(item)}
                                                className="text-left min-w-0"
                                            >
                                                <div className="font-bold text-sm truncate">{item.locationName}</div>
                                                <div className="text-xs text-ink-500 truncate mt-0.5">{item.address}</div>
                                                <div className="flex items-center justify-between mt-3">
                                                    <span className="text-[10px] text-ink-400">
                                                        {new Date(item.createdAt).toLocaleDateString('en-IN')}
                                                    </span>
                                                    <span className={`ag-chip text-xs font-bold ${aoiScoreTone(item.aoiScore)}`}>
                                                        {Number(item.aoiScore).toFixed(1)}
                                                    </span>
                                                </div>
                                            </button>
                                            <div className="flex items-center gap-2 pt-2 border-t border-ink-100">
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedHistory(item)}
                                                    className="ag-btn text-xs flex-1 justify-center"
                                                >
                                                    Open
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => downloadHistoryItem(item)}
                                                    disabled={downloadingHistory}
                                                    className="ag-btn-ghost text-xs flex-1 justify-center"
                                                    title="Download PDF"
                                                >
                                                    <Download className="h-3.5 w-3.5" />
                                                    {downloadingHistory ? '…' : 'Download'}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
